import { Proposition } from '@ecosystem/newron-client-collections';
import { Params } from '../../types';
import { InsightData } from './types';

export const getPropositionFromContextURL = (contextURL: string): { baseURL: string; proposition: Proposition } => {
  const [baseURL, queryParams] = contextURL.split('?');
  const propositionParam = queryParams?.split('&').find(param => param.startsWith('proposition='));
  if (!propositionParam) {
    return {
      baseURL,
      proposition: Proposition.Market,
    };
  }
  const propositionValue = propositionParam.split('=')?.[1];
  if (
    !propositionValue ||
    (propositionValue !== Proposition.Market && propositionValue !== Proposition.DistributionPanel)
  ) {
    return {
      baseURL,
      proposition: Proposition.Market,
    };
  }
  return {
    baseURL,
    proposition: propositionValue,
  };
};

export const removeContextItemsByKey = ({ paramsForContext, specificKey = 'f:region_name' }) => {
  if (!paramsForContext || Object.keys(paramsForContext).length === 0) return;
  Object.keys(paramsForContext).forEach(key => {
    if (key.startsWith(specificKey)) {
      delete paramsForContext[key];
    }
  });
};

export const removeDataItemsByKey = ({ paramsForData, specificKey = 'region_name' }) => {
  if (!paramsForData || Object.keys(paramsForData).length === 0) return;
  Object.keys(paramsForData).forEach(key => {
    if (key.startsWith(specificKey)) {
      delete paramsForData[key];
    }
  });
};

export const isMultiCell = paramsForContext => {
  return paramsForContext?.cell.length > 1;
};

export const deepMergeConcat = <T>(...objects: T[]): T => {
  const result: any = {};

  for (const obj of objects) {
    for (const key in obj) {
      const existing = result[key];
      const incoming = (obj as any)[key];

      if (Array.isArray(existing) && Array.isArray(incoming)) {
        // concat arrays
        result[key] = [...existing, ...incoming];
      } else if (
        typeof existing === 'object' &&
        typeof incoming === 'object' &&
        !Array.isArray(existing) &&
        !Array.isArray(incoming)
      ) {
        // deep merge nested objects
        result[key] = deepMergeConcat(existing, incoming);
      } else {
        // take new if not exists
        result[key] = existing !== undefined ? existing : incoming;
      }
    }
  }

  return result;
};

const checkCustomFeatureInParam = (paramValue: string[]): boolean => {
  return paramValue.some(value => value.startsWith('segment_custom_feature'));
};

export const reformatCustomFeatureTiers = (params: Params): Params => {
  const groupingValue = params?.grouping;
  const sgSegmentValue = params?.sgSegment;

  const hasCustomInGrouping = groupingValue && checkCustomFeatureInParam(groupingValue);
  const hasCustomInSgSegment = sgSegmentValue && checkCustomFeatureInParam(sgSegmentValue);

  // no need to reformat as these scenarios are not broken
  if (hasCustomInGrouping === hasCustomInSgSegment || !Array.isArray(params?.customFeatureTiers)) {
    return params;
  }

  const prefix = hasCustomInGrouping ? 'grouping' : 'sgSegment';

  // if there is already a correctly prefixed custom feature tier, return as is, otherwise we would be mistakenly adding prefixes to old tiers that were not properly cleared from preferences
  const hasValidCustomTier = params.customFeatureTiers.find(tier => tier.startsWith(`${prefix}:`));

  if (hasValidCustomTier) {
    return params;
  }

  return {
    ...params,
    customFeatureTiers: params.customFeatureTiers.map(tier => (tier.startsWith('name:') ? `${prefix}:${tier}` : tier)),
  };
};

export function isBulkExportPage(): boolean {
  const collectionContext = sessionStorage.getItem('collection_context');
  return collectionContext ? collectionContext === 'export' : window?.location?.href.includes('/insights/');
}

export const reformatCorePageSettings = (insightData: InsightData): InsightData => {
  const { corePageSettings } = insightData;

  return {
    ...insightData,
    corePageSettings: {
      ...corePageSettings,
      page: {
        ...corePageSettings.page,
        url: formatUrl(corePageSettings.page.url),
      },
      context: {
        ...corePageSettings.context,
        url: formatUrl(corePageSettings.context.url),
      },
      data: {
        ...corePageSettings.data,
        url: formatUrl(corePageSettings.data.url),
      },
      collection: {
        ...corePageSettings.collection,
        url: formatUrl(corePageSettings.collection.url),
      },
    },
  };
};

export const removeOriginFromCorePageSettings = (insightData: InsightData): InsightData => {
  const { corePageSettings } = insightData;

  return {
    ...insightData,
    corePageSettings: {
      ...corePageSettings,
      page: {
        ...corePageSettings.page,
        url: removeOriginFromUrl(corePageSettings.page.url),
      },
      context: {
        ...corePageSettings.context,
        url: removeOriginFromUrl(corePageSettings.context.url),
      },
      data: {
        ...corePageSettings.data,
        url: removeOriginFromUrl(corePageSettings.data.url),
      },
      collection: {
        ...corePageSettings.collection,
        url: removeOriginFromUrl(corePageSettings.collection.url),
      },
    },
  };
};

const formatUrl = (url: string, includeQuery = false): string => {
  try {
    // Try to create URL object - if it fails, it's a relative path
    const urlObj = new URL(url);
    // If successful, extract path (and query if needed) and add current origin
    return window.location.origin + urlObj.pathname + (includeQuery ? urlObj.search : '');
  } catch {
    // URL is already relative, just add origin if it doesn't start with origin
    if (url.startsWith(window.location.origin)) {
      return url;
    }
    return window.location.origin + (url.startsWith('/') ? url : `/${url}`);
  }
};

const removeOriginFromUrl = (url: string): string => {
  try {
    // Try to create URL object - if it fails, it's a relative path
    const urlObj = new URL(url);
    // If successful, extract path (and query if needed) and add current origin
    return urlObj.pathname + urlObj.search;
  } catch {
    return url.startsWith('/') ? url : `/${url}`;
  }
};
